import React, { useState } from 'react';
import { Sliders, Activity, Heart, Info } from 'lucide-react';
import { toast } from 'sonner';
import { useFetch, fmtDate, fmtEurFromEur } from '../utils';
import { adminApi } from '../api';
import { Section } from '../_primitives/Section';
import { DataTable, type Column } from '../_primitives/DataTable';
import { ConfirmDialog } from '../_primitives/ConfirmDialog';
import type { GlobalPreferencesResponse, PreferencesHealthResponse, PreferencesUsersResponse } from '../types';

type PatternRow = GlobalPreferencesResponse['patterns'][number];
type HealthRow = PreferencesHealthResponse['reports'][number];
type SpendRow = PreferencesUsersResponse['users'][number];

const patternCols: Column<PatternRow>[] = [
  { key: 'roomType', label: 'Ruimte', sortValue: (r) => r.roomType, searchValue: (r) => r.roomType,
    render: (r) => <span className="text-ink/85 dark:text-white/85">{r.roomType}</span> },
  { key: 'style', label: 'Stijl', sortValue: (r) => r.style, searchValue: (r) => r.style,
    render: (r) => <span className="text-ink/65 dark:text-white/65">{r.style}</span> },
  { key: 'kind', label: 'Soort', sortValue: (r) => r.kind, searchValue: (r) => r.kind,
    render: (r) => <span className="text-ink/65 dark:text-white/65">{r.kind}</span> },
  { key: 'value', label: 'Waarde', sortValue: (r) => r.value, searchValue: (r) => r.value,
    render: (r) => <span className="font-mono text-ink/85 dark:text-white/85 truncate max-w-[24ch] inline-block" title={r.value}>{r.value}</span> },
  { key: 'supportCount', label: 'Support', align: 'right', sortValue: (r) => r.supportCount,
    render: (r) => <span className="font-mono text-ink/75 dark:text-white/75">{r.supportCount}</span> },
  { key: 'confidenceWeightedScore', label: 'Confidence', align: 'right', sortValue: (r) => r.confidenceWeightedScore,
    render: (r) => <span className="font-mono text-ink/65 dark:text-white/65">{r.confidenceWeightedScore.toFixed(2)}</span> },
  { key: 'lastSeenAt', label: 'Laatst gezien', sortValue: (r) => r.lastSeenAt,
    render: (r) => <span className="text-ink/55 dark:text-white/55">{fmtDate(r.lastSeenAt)}</span> },
];

const healthCols: Column<HealthRow>[] = [
  { key: 'week_start', label: 'Week', sortValue: (r) => r.week_start,
    render: (r) => <span className="font-mono text-ink/85 dark:text-white/85">{fmtDate(r.week_start)}</span> },
  { key: 'ab_variant', label: 'Variant', sortValue: (r) => r.ab_variant,
    render: (r) => <span className="text-ink/65 dark:text-white/65">{r.ab_variant}</span> },
  { key: 'render_count', label: 'Renders', align: 'right', sortValue: (r) => r.render_count,
    render: (r) => <span className="font-mono text-ink/75 dark:text-white/75">{r.render_count}</span> },
  { key: 'thumbs_up_rate', label: 'Thumbs-up %', align: 'right', sortValue: (r) => r.thumbs_up_rate ?? 0,
    render: (r) => <span className="font-mono text-ink/65 dark:text-white/65">{r.thumbs_up_rate != null ? (r.thumbs_up_rate * 100).toFixed(1) + '%' : '—'}</span> },
  { key: 'rules_added_total', label: 'Rules +', align: 'right', sortValue: (r) => r.rules_added_total,
    render: (r) => <span className="font-mono text-green-600 dark:text-green-400">{r.rules_added_total}</span> },
  { key: 'rules_removed_total', label: 'Rules −', align: 'right', sortValue: (r) => r.rules_removed_total,
    render: (r) => <span className="font-mono text-red-600 dark:text-red-400">{r.rules_removed_total}</span> },
  { key: 'users_flagged_high_churn', label: 'Churn-flag', align: 'right', sortValue: (r) => r.users_flagged_high_churn,
    render: (r) => <span className="font-mono text-amber-600 dark:text-amber-400">{r.users_flagged_high_churn}</span> },
];

const spendCols = (onReset: (u: SpendRow) => void): Column<SpendRow>[] => [
  { key: 'email', label: 'Email', sortValue: (u) => u.email, searchValue: (u) => u.email,
    render: (u) => <span className="font-medium text-ink dark:text-white">{u.email}</span> },
  { key: 'ab_variant', label: 'Variant',
    render: (u) => <span className="text-ink/65 dark:text-white/65">{u.ab_variant ?? '—'}</span> },
  { key: 'week_spend', label: 'Spend (7d)', align: 'right', sortValue: (u) => u.week_spend,
    render: (u) => <span className="font-mono text-ink/85 dark:text-white/85">{fmtEurFromEur(u.week_spend)}</span> },
  { key: 'drift_snapshots', label: 'Drift (14d)', align: 'right', sortValue: (u) => u.drift_snapshots,
    render: (u) => <span className="font-mono text-ink/65 dark:text-white/65">{u.drift_snapshots}</span> },
  { key: 'last_snapshot', label: 'Laatste snapshot', sortValue: (u) => u.last_snapshot ?? '',
    render: (u) => <span className="text-ink/55 dark:text-white/55">{fmtDate(u.last_snapshot)}</span> },
  { key: '__actions', label: '',
    render: (u) => (
      <button type="button" onClick={(e) => { e.stopPropagation(); onReset(u); }} className="text-[11px] font-semibold text-red-600 dark:text-red-400 hover:underline">
        Reset…
      </button>
    ) },
];

interface Props {
  trigger?: { kind: 'reset-preferences' | null; nonce: number };
}

export const PreferencesTab: React.FC<Props> = ({ trigger }) => {
  const patterns = useFetch(adminApi.globalPreferences);
  const health = useFetch(adminApi.preferencesHealth);
  const spend = useFetch(adminApi.preferencesUsers);

  const [resetTarget, setResetTarget] = useState<SpendRow | null>(null);
  const [reason, setReason] = useState('');

  React.useEffect(() => {
    if (trigger?.kind === 'reset-preferences') toast.info('Klik op "Reset…" naast een gebruiker in de spend-tabel.');
  }, [trigger]);

  const runReset = async () => {
    if (!resetTarget) return;
    if (!reason.trim()) { toast.error('Reden verplicht voor audit-log'); return; }
    try {
      await adminApi.resetPreferences(resetTarget.id, reason);
      toast.success(`Voorkeuren gereset voor ${resetTarget.email}`);
      setResetTarget(null);
      setReason('');
      spend.refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Reset mislukt');
    }
  };

  return (
    <div className="space-y-4">
      <Section title="Globale voorkeur-patronen" subtitle={patterns.data ? `${patterns.data.total} patronen, min ${patterns.data.minDistinctUsers} gebruikers` : undefined} toolbar={<Sliders size={14} className="text-ink/45 dark:text-white/45" />}>
        {patterns.data && patterns.data.total === 0 && !patterns.loading && (
          <div
            role="note"
            className="mb-3 flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-[12.5px] text-blue-900 dark:border-blue-500/30 dark:bg-blue-500/10 dark:text-blue-200"
          >
            <Info size={14} className="mt-0.5 shrink-0" aria-hidden="true" />
            <span>
              Patronen verschijnen automatisch zodra ten minste{' '}
              <strong>{patterns.data.minDistinctUsers} gebruikers</strong> dezelfde voorkeur hebben opgeslagen
              (per ruimte × stijl × soort). Tot die drempel is gehaald blijft deze tabel leeg — dat is normaal en
              vereist geen actie.
            </span>
          </div>
        )}
        <DataTable
          rows={patterns.data?.patterns ?? []}
          loading={patterns.loading}
          error={patterns.error}
          onRetry={patterns.refresh}
          columns={patternCols}
          rowKey={(r) => `${r.roomType}-${r.style}-${r.kind}-${r.value}`}
          searchPlaceholder="Zoek op ruimte, stijl, waarde…"
        />
      </Section>

      <Section title="A/B health-rapporten" subtitle="Laatste 8 weken × cohorten" toolbar={<Heart size={14} className="text-ink/45 dark:text-white/45" />}>
        <DataTable
          rows={health.data?.reports ?? []}
          loading={health.loading}
          error={health.error}
          onRetry={health.refresh}
          columns={healthCols}
          rowKey={(r) => r.id}
          hideSearch
        />
      </Section>

      <Section title="Top spend-gebruikers" subtitle="Top 20 per 7d EUR + drift-snapshot count (14d)" toolbar={<Activity size={14} className="text-ink/45 dark:text-white/45" />}>
        <DataTable
          rows={spend.data?.users ?? []}
          loading={spend.loading}
          error={spend.error}
          onRetry={spend.refresh}
          columns={spendCols(setResetTarget)}
          rowKey={(u) => u.id}
          searchPlaceholder="Zoek op email…"
        />
      </Section>

      <ConfirmDialog
        open={!!resetTarget}
        destructive
        typedConfirmation="RESET"
        title={`Voorkeuren resetten — ${resetTarget?.email ?? ''}`}
        description={
          <div className="space-y-3">
            <p>Wist staging_preferences en herevalueert ab_variant. Snapshot vóór de reset wordt automatisch in staging_preferences_history geschreven.</p>
            <textarea
              placeholder="Reden (verplicht voor audit-log)…"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 text-sm rounded-lg border border-black/10 dark:border-white/15 bg-white dark:bg-ink/60 dark:text-white focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
            />
          </div>
        }
        confirmLabel="Reset"
        onConfirm={runReset}
        onCancel={() => { setResetTarget(null); setReason(''); }}
      />
    </div>
  );
};
